import React, { useState } from 'react';
import MaturityUpload from './MaturityUpload';

/* ═══════════════════════════════════════════════════════════════
   Weighted Average Maturity (WAM) Calculator

   Form 8038 definition — issue price weighted:
   WAM = sum(issuePrice x years) / totalIssuePrice
   where years = days30360(datedDate, maturityDate) / 360
   ═══════════════════════════════════════════════════════════════ */

// ── 30/360 Day Count (US Bond Basis) — identical to YieldCalculator ──
function days30360(d1, d2) {
  let y1 = d1.getFullYear(), m1 = d1.getMonth() + 1, d1d = d1.getDate();
  let y2 = d2.getFullYear(), m2 = d2.getMonth() + 1, d2d = d2.getDate();
  if (d1d === 31) d1d = 30;
  if (d2d === 31 && d1d === 30) d2d = 30;
  return 360 * (y2 - y1) + 30 * (m2 - m1) + (d2d - d1d);
}

function parseDate(s) {
  if (!s) return null;
  const parts = s.split(/[-/]/);
  if (parts.length !== 3) return null;
  let m, d, y;
  if (parts[0].length === 4) { y = +parts[0]; m = +parts[1]; d = +parts[2]; }
  else { m = +parts[0]; d = +parts[1]; y = +parts[2]; }
  return new Date(y, m - 1, d);
}

function fmtDate(dt) {
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
}

// ── Styling (matches YieldCalculator exactly) ────────────────
const sHdr = { background: 'var(--ribbon-bg)', color: 'var(--ribbon-text)', padding: '6px 14px',
  fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: '4px', marginBottom: '12px', marginTop: '4px' };

const sInput = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)',
  borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)' };

const sLabel = { display: 'block', fontSize: '11px', fontWeight: '600',
  color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' };

const sBtn = { padding: '8px 18px', borderRadius: '4px', border: 'none', cursor: 'pointer',
  fontSize: '13px', fontWeight: '600', fontFamily: 'var(--font-body)', transition: 'var(--transition-fast)' };

const sBtnPrimary = { ...sBtn, background: 'var(--accent-color)', color: '#fff' };
const sBtnSecondary = { ...sBtn, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };

const sTable = { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'var(--font-body)' };
const sTh = { padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-secondary)',
  borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)' };
const sTd = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid var(--border-light)',
  fontFamily: "'DM Sans', monospace", fontSize: '12px' };

const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

// ── WAM calculation ──────────────────────────────────────────
function calcWAM(datedDt, rows) {
  let totalIP = 0;
  let weightedSum = 0;
  const details = [];

  for (const row of rows) {
    const matDt = parseDate(row.maturityDate);
    const ip = parseFloat(String(row.issuePrice).replace(/,/g, ''));
    if (!matDt || isNaN(ip) || ip === 0) continue;

    const days = days30360(datedDt, matDt);
    const years = days / 360;
    const weighted = ip * years;
    totalIP += ip;
    weightedSum += weighted;
    details.push({ date: matDt, issuePrice: ip, days, years, weighted });
  }

  const wam = totalIP > 0 ? weightedSum / totalIP : 0;
  return { wam, totalIP, details };
}

// ── Maturity table sub-component ─────────────────────────────
const MaturityTable = ({ label, rows, setRows, result }) => {
  const addRow = () => setRows(prev => [...prev, { maturityDate: '', issuePrice: '' }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const update = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  return (
    <div style={{ flex: '1 1 420px', minWidth: '380px' }}>
      <div style={sHdr}>{label}</div>
      <table style={sTable}>
        <thead>
          <tr>
            <th style={{ ...sTh, textAlign: 'left' }}>Maturity Date</th>
            <th style={sTh}>Issue Price ($)</th>
            {result && <th style={sTh}>Years</th>}
            {result && <th style={sTh}>Weighted</th>}
            <th style={{ ...sTh, width: '36px' }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...sTd, textAlign: 'left' }}>
                <input type="text" value={r.maturityDate} onChange={e => update(i, 'maturityDate', e.target.value)}
                  placeholder="MM/DD/YYYY" style={{ ...sInput, width: '120px', padding: '4px 6px', fontSize: '12px' }} />
              </td>
              <td style={sTd}>
                <input type="text" value={r.issuePrice} onChange={e => update(i, 'issuePrice', e.target.value)}
                  placeholder="0.00" style={{ ...sInput, width: '130px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
              </td>
              {result && result.details[i] && (
                <>
                  <td style={sTd}>{result.details[i].years.toFixed(4)}</td>
                  <td style={sTd}>{fmt$(result.details[i].weighted)}</td>
                </>
              )}
              {result && !result.details[i] && (<><td style={sTd}>-</td><td style={sTd}>-</td></>)}
              <td style={{ ...sTd, textAlign: 'center' }}>
                {rows.length > 1 && (
                  <button onClick={() => removeRow(i)} title="Remove"
                    style={{ background: 'none', border: 'none', color: 'var(--error-color)',
                      cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>x</button>
                )}
              </td>
            </tr>
          ))}
          {result && (
            <tr style={{ fontWeight: '700', borderTop: '2px solid var(--border-color)' }}>
              <td style={{ ...sTd, textAlign: 'left' }}>TOTAL</td>
              <td style={sTd}>{fmt$(result.totalIP)}</td>
              <td style={sTd}></td>
              <td style={sTd}></td>
              <td style={sTd}></td>
            </tr>
          )}
        </tbody>
      </table>
      <button onClick={addRow} style={{ ...sBtnSecondary, marginTop: '8px', fontSize: '12px' }}>+ Add Maturity</button>

      {result && (
        <div style={{ marginTop: '12px', padding: '12px 16px', background: 'var(--bg-secondary)',
          borderRadius: '6px', border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase',
            letterSpacing: '0.06em', marginBottom: '4px' }}>{label} WAM</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent-color)',
            fontFamily: 'var(--font-reading)' }}>
            {result.wam.toFixed(4)} years
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            Total issue price: {fmt$(result.totalIP)}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────
const WAMCalculator = () => {
  const [datedDate, setDatedDate] = useState('');
  const [newIssueRows, setNewIssueRows] = useState([{ maturityDate: '', issuePrice: '' }]);
  const [refundedRows, setRefundedRows] = useState([{ maturityDate: '', issuePrice: '' }]);
  const [newResult, setNewResult] = useState(null);
  const [refResult, setRefResult] = useState(null);
  const [error, setError] = useState('');

  const uploadColumns = [
    { key: 'maturity_date', label: 'Maturity Date' },
    { key: 'par_amount', label: 'Par / Issue Price' },
    { key: 'coupon', label: 'Coupon (%)' },
    { key: 'price', label: 'Price ($)' },
  ];

  const handlePopulateNew = (parsed) => {
    const mapped = parsed
      .filter(r => r.maturity_date || r.par_amount)
      .map(r => ({
        maturityDate: r.maturity_date || '',
        issuePrice: r.price != null ? String(r.price) : (r.par_amount != null ? String(r.par_amount) : ''),
      }));
    if (mapped.length > 0) setNewIssueRows(mapped);
  };

  const handlePopulateRefunded = (parsed) => {
    const mapped = parsed
      .filter(r => r.maturity_date || r.par_amount)
      .map(r => ({
        maturityDate: r.maturity_date || '',
        issuePrice: r.price != null ? String(r.price) : (r.par_amount != null ? String(r.par_amount) : ''),
      }));
    if (mapped.length > 0) setRefundedRows(mapped);
  };

  const calculate = () => {
    setError('');
    setNewResult(null);
    setRefResult(null);

    const datedDt = parseDate(datedDate);
    if (!datedDt) { setError('Invalid dated/delivery date'); return; }

    const hasNewRows = newIssueRows.some(r => r.maturityDate && r.issuePrice);
    const hasRefRows = refundedRows.some(r => r.maturityDate && r.issuePrice);

    if (!hasNewRows && !hasRefRows) { setError('Enter at least one maturity in either section'); return; }

    if (hasNewRows) {
      const nr = calcWAM(datedDt, newIssueRows);
      if (nr.details.length === 0) { setError('New issue: no valid rows'); return; }
      setNewResult(nr);
    }
    if (hasRefRows) {
      const rr = calcWAM(datedDt, refundedRows);
      if (rr.details.length === 0) { setError('Refunded bonds: no valid rows'); return; }
      setRefResult(rr);
    }
  };

  const exportCSV = () => {
    let csv = 'Section,Maturity Date,Issue Price,Days (30/360),Years,Weighted\n';
    const write = (label, result) => {
      if (!result) return;
      for (const d of result.details) {
        csv += `${label},${fmtDate(d.date)},${d.issuePrice.toFixed(2)},${d.days},${d.years.toFixed(4)},${d.weighted.toFixed(2)}\n`;
      }
      csv += `${label} TOTAL,,${result.totalIP.toFixed(2)},,,WAM: ${result.wam.toFixed(4)}\n`;
    };
    write('New Issue', newResult);
    write('Refunded Bonds', refResult);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'wam_calculation.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportReport = () => {
    let rpt = `WEIGHTED AVERAGE MATURITY CALCULATION\n${'='.repeat(50)}\n\n`;
    rpt += `Dated/Delivery Date: ${datedDate}\n\n`;
    const writeSection = (label, result) => {
      if (!result) return;
      rpt += `${label.toUpperCase()}\n${'-'.repeat(50)}\n`;
      rpt += 'Maturity Date     Issue Price          Days    Years    Weighted\n';
      for (const d of result.details) {
        rpt += `${fmtDate(d.date).padEnd(18)}${fmt$(d.issuePrice).padStart(20)}${String(d.days).padStart(8)}${d.years.toFixed(4).padStart(10)}${fmt$(d.weighted).padStart(16)}\n`;
      }
      rpt += `${'TOTAL'.padEnd(18)}${fmt$(result.totalIP).padStart(20)}\n`;
      rpt += `WAM: ${result.wam.toFixed(4)} years\n\n`;
    };
    writeSection('New Issue', newResult);
    writeSection('Refunded Bonds', refResult);
    if (newResult && refResult) {
      rpt += `SUMMARY\n${'-'.repeat(50)}\n`;
      rpt += `New Issue WAM:      ${newResult.wam.toFixed(4)} years\n`;
      rpt += `Refunded Bonds WAM: ${refResult.wam.toFixed(4)} years\n`;
      rpt += `Difference:         ${(newResult.wam - refResult.wam).toFixed(4)} years\n`;
    }
    const blob = new Blob([rpt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'wam_report.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', fontFamily: 'var(--font-body)',
      maxWidth: '1100px', margin: '0 auto', width: '100%' }}>

      <h2 style={{ fontFamily: 'var(--font-reading)', fontSize: '22px', fontWeight: '600',
        color: 'var(--text-primary)', marginBottom: '4px' }}>
        Weighted Average Maturity Calculator
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Form 8038 WAM — issue price weighted, 30/360 day count (US Bond Basis)
      </p>

      {/* Example + Dated Date row */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 200px' }}>
          <label style={sLabel}>Dated / Delivery Date</label>
          <input type="text" value={datedDate} onChange={e => setDatedDate(e.target.value)}
            placeholder="MM/DD/YYYY" style={sInput} />
        </div>
        <button onClick={calculate} style={sBtnPrimary}>Calculate WAM</button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* File upload for New Issue */}
      <div style={sHdr}>Import Maturity Schedule</div>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ flex: '1 1 420px', minWidth: '380px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>New Issue</div>
          <MaturityUpload columns={uploadColumns} onPopulate={handlePopulateNew} />
        </div>
        <div style={{ flex: '1 1 420px', minWidth: '380px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Refunded Bonds</div>
          <MaturityUpload columns={uploadColumns} onPopulate={handlePopulateRefunded} />
        </div>
      </div>

      {/* Side-by-side tables */}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <MaturityTable label="New Issue" rows={newIssueRows} setRows={setNewIssueRows} result={newResult} />
        <MaturityTable label="Refunded Bonds" rows={refundedRows} setRows={setRefundedRows} result={refResult} />
      </div>

      {/* Summary */}
      {(newResult || refResult) && (
        <>
          <div style={{ ...sHdr, marginTop: '24px' }}>Summary</div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {newResult && (
              <div style={{ flex: '1 1 200px', padding: '14px 18px', background: 'var(--bg-secondary)',
                borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', marginBottom: '6px' }}>New Issue WAM</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-color)',
                  fontFamily: 'var(--font-reading)' }}>{newResult.wam.toFixed(4)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>years</div>
              </div>
            )}
            {refResult && (
              <div style={{ flex: '1 1 200px', padding: '14px 18px', background: 'var(--bg-secondary)',
                borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', marginBottom: '6px' }}>Refunded Bonds WAM</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-color)',
                  fontFamily: 'var(--font-reading)' }}>{refResult.wam.toFixed(4)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>years</div>
              </div>
            )}
            {newResult && refResult && (
              <div style={{ flex: '1 1 200px', padding: '14px 18px', background: 'var(--bg-secondary)',
                borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', marginBottom: '6px' }}>Difference (New - Refunded)</div>
                <div style={{ fontSize: '28px', fontWeight: '700',
                  color: (newResult.wam - refResult.wam) >= 0 ? 'var(--success-color)' : 'var(--warning-color)',
                  fontFamily: 'var(--font-reading)' }}>
                  {(newResult.wam - refResult.wam) >= 0 ? '+' : ''}{(newResult.wam - refResult.wam).toFixed(4)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>years</div>
              </div>
            )}
          </div>

          {/* Export buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
            <button onClick={exportCSV} style={sBtnSecondary}>Export CSV</button>
            <button onClick={exportReport} style={sBtnSecondary}>Export Report</button>
          </div>
        </>
      )}
    </div>
  );
};

export default WAMCalculator;
